import React, {ReactNode, useState } from "react";
import s from "./InfoButton.module.scss";
import {FontIcon, Icon, isIcon} from "../FontIcon";
import {localStorageService} from "../../../services/storage/local-storage";
import {GenericModal} from "../genericModal/GenericModal";

interface IInfoButtonProps {
  additionalClassName?: string;
  ariaLabel?: string;
  defaultState?: "open" | "closed";
  icon?: Icon | ReactNode;
  persistState?: boolean; // If true, the open/closed state will be saved in localStorage. If provided the storageKeyId must also be provided.
  storageKeyId?: string; // Unique identifier for localStorage key. Only used if persistState is true.
  text: string | ReactNode,
  title?: string;
}

type InfoButtonState = "open" | "closed";

export default function InfoButton({
  ariaLabel,
  defaultState = "closed",
  icon,
  persistState = false,
  storageKeyId,
  text,
  title
}: IInfoButtonProps) {
  const [state, setState] = useState<InfoButtonState>(defaultState);

  const setInMemoryStateAndTryToSetLocalStorageState = (newState: InfoButtonState) => {
    // Only call setState if the state is actually changing, to avoid an infinite loop
    if (state !== newState) {
      setState(newState);
    }
    if (persistState && storageKeyId && storageKeyId?.length > 0) {
      localStorageService.setJsonItem<boolean>(storageKeyId, newState === "open");
    }
  };

  const getStateFromLocalStorage = (): boolean | undefined => {
    if (persistState && storageKeyId && storageKeyId?.length > 0) {
      const isOpen = localStorageService.getJsonItem<boolean>(storageKeyId);
      return isOpen;
    }

    return undefined;
  }

  const tryLoadStateFromLocalStorage = () => {
    const isOpen= getStateFromLocalStorage();

    if (isOpen !== undefined) {
      const stateFromLocalStorage = isOpen ? "open" : "closed";
      if (stateFromLocalStorage !== state) {
        setState(stateFromLocalStorage);
      }
    }
  }

  tryLoadStateFromLocalStorage();

  return (
    <>
      <button className={"info-icon"}
        onClick={()=> {
          setInMemoryStateAndTryToSetLocalStorageState("open");
        }}
        aria-label={ariaLabel}>
        <FontIcon icon={"icon-info"}/>
      </button>
      <HelpModal
        state={state}
        setInMemoryStateAndTryToSetLocalStorageState={setInMemoryStateAndTryToSetLocalStorageState}
        icon={icon}
        text={text}
        title={title}
      />
    </>
  );
}

interface IHelpModalProps {
  icon?: Icon | ReactNode,
  state: InfoButtonState;
  setInMemoryStateAndTryToSetLocalStorageState: (state: InfoButtonState) => void;
  text: string | ReactNode,
  title?: string;
}

function HelpModal({state, setInMemoryStateAndTryToSetLocalStorageState, icon, text, title}: IHelpModalProps) {
  if(state !== "open") {
    return null;
  }

  return <GenericModal
    backgroundColor={"purple"}
    canShow={true}
    maxWidth={"min(85vw, 1500px)"}
    onClose={() => { setInMemoryStateAndTryToSetLocalStorageState("closed"); }}
    showCloseButtonInBody={true}
    navigationNote={"B"}
  >
    <div className={s.container}>
      { icon && React.isValidElement(icon)
        ?
        <div className={s.iconNode}>
          {icon}
        </div>
        :
        null
      }
      { icon && isIcon(icon)
        ?
        <div className={s.icon}>
          <FontIcon additionalClassName={s.fontIcon} icon={icon}/>
        </div>
        :
        null
      }
      { title
        ?
        <div className={s.title}>
          {title}
        </div>
        :
        null
      }
      <div className={s.body}>
        {text}
      </div>
    </div>
  </GenericModal>
}
